import { fitTitle, fitDescription } from "@/lib/seo-text";
import { createFileRoute, Link } from "@tanstack/react-router";

import { DutyCalculator } from "@/components/duty-calculator";
import { EnquiryForm } from "@/components/enquiry-form";
import { CtaBand, FaqList, PageHero, PeopleStrip } from "@/components/page-parts";
import { FEES, FIRM, faqSchema } from "@/lib/firm";

const FAQS = [
  {
    q: "How much is stamp duty in Queensland?",
    a: "Transfer duty in Queensland is charged on a sliding scale on the greater of the purchase price and the market value. On the general rate it is nil up to $5,000, then 1.5% to $75,000, $1,050 plus 3.5% to $540,000, $17,325 plus 4.5% to $1,000,000, and $38,025 plus 5.75% above that. If you are going to live in the property the home concession applies instead, charging $1.00 per $100 on the first $350,000.",
  },
  {
    q: "Do first home buyers pay stamp duty in Queensland?",
    a: "Usually not. First home buyers of an established home pay no duty under $700,000, with a partial rebate phasing out at $800,000. For contracts signed from 1 May 2025, first home buyers of a brand-new home or an off-the-plan purchase pay no transfer duty at all, with no value cap. First home vacant land is duty free under $350,000, phasing out to $500,000.",
  },
  {
    q: "When is transfer duty payable?",
    a: "Duty is assessed once the contract is unconditional and is paid at or before settlement, usually through your solicitor's PEXA workspace. The statutory deadline is 30 days from the liability date, so a long settlement does not push the payment out indefinitely.",
  },
  {
    q: "Is there extra duty for foreign buyers?",
    a: "Yes. Additional foreign acquirer duty (AFAD) of 8% of the value applies on residential land acquired by a foreign individual, foreign company or foreign trust. It is charged on top of ordinary transfer duty, so it is a large number on a modest property.",
  },
  {
    q: "What else do I pay besides duty?",
    a: `Titles Registry lodgement fees on the transfer, searches and PEXA fees (typically $350 to $750 all up), and legal fees. Ours are fixed: ${FEES.purchase} incl. GST on a purchase and ${FEES.sale} incl. GST on a sale, with a free contract review before you sign.`,
  },
  {
    q: "Can I lose a concession after settlement?",
    a: "Yes. The home and first home concessions require you to move in within 12 months and not dispose of the property (including renting all of it out) for the following 12 months. If you breach those conditions you must notify the Commissioner within 28 days and repay the concession. We explain the conditions before you commit.",
  },
];

export const Route = createFileRoute("/stamp-duty-calculator-qld")({
  head: () => ({
    meta: [
      { title: fitTitle(`Transfer Duty Queensland Calculator 2026 (Stamp Duty) | ${FIRM.name}`) },
      {
        name: "description",
        content:
          fitDescription("Free Queensland stamp duty calculator. Estimate transfer duty with the home and first home concessions, foreign buyer surcharge, registry fees and fixed legal fees."),
      },
      { property: "og:title", content: fitTitle(`Queensland Stamp Duty Calculator | ${FIRM.name}`) },
      {
        property: "og:description",
        content:
          fitDescription("Work out your Queensland transfer duty, concessions and total purchase costs in a few seconds."),
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://coastsidelaw.com.au/stamp-duty-calculator-qld" },
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://coastsidelaw.com.au/stamp-duty-calculator-qld" }],
    scripts: [{ type: "application/ld+json", children: JSON.stringify(faqSchema(FAQS)) }],
  }),
  component: StampDutyPage,
});

function StampDutyPage() {
  return (
    <>
      <PageHero
        eyebrow="Free tool · Queensland"
        title="Queensland transfer duty calculator (stamp duty)"
        cta="Confirm my duty and costs"
        lead="This calculator covers transfer duty on Queensland homes and land, not vehicle registration duty. Transfer duty is usually the single biggest cost on top of the purchase price. Put your numbers in below and you will see the duty, the concession that applies to you, and what the whole purchase costs once registry fees, searches and legal fees are added."
      />

      <section className="mx-auto max-w-3xl px-5 py-16">
        <DutyCalculator />

        <h2 className="font-serif text-3xl text-foreground">How Queensland transfer duty works</h2>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          The Queensland Revenue Office administers transfer duty. Check its{" "}
          <a href="https://qro.qld.gov.au/duties/transfer-duty/" className="font-semibold text-primary underline">
            current transfer duty rates and concession requirements
          </a>{" "}
          alongside this estimate. For a contract involving a company, trust or unusual ownership
          arrangement, ask us to confirm the assessment rather than relying on the calculator alone.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Duty is imposed under the Duties Act 2001 (Qld) and assessed on the greater of the price
          you pay and the market value of the property. Which scale applies depends on what you are
          doing with the property, not on what you call it in the contract. An investor pays the
          general rate. Someone who will actually live in the home pays the home concession rate of
          $1.00 per $100 on the first $350,000. A first home buyer gets a rebate on top of that
          which, for an established home, wipes duty out entirely under $700,000.
        </p>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          Concessions come with conditions. You must move in within 12 months of settlement and you
          must not dispose of the property — including renting the whole of it out — for the 12
          months after that. Breach either and you repay the concession, with a 28-day obligation to
          tell the Commissioner. That is worth understanding before you sign, not after.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Getting it right on your contract</h2>
        <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
          The calculator is an estimate. The assessed figure turns on how the contract is drawn: who
          the buyer is named as, whether a trust or company is involved, whether chattels are priced
          separately, whether the transaction is part of a related-party arrangement, and the date
          the contract was signed. We check all of that as part of the free pre-signing contract
          review and confirm the exact duty payable before you commit.
        </p>

        <p className="mt-4 text-base leading-relaxed text-muted-foreground">
          Planning a purchase? Compare <Link to="/conveyancing-cost-qld" className="font-semibold text-primary underline">Queensland conveyancing fees and other buying costs</Link>{" "}
          alongside your duty estimate. If you have already signed, check the{" "}
          <Link to="/cooling-off-period-qld" className="font-semibold text-primary underline">Queensland cooling-off period</Link>{" "}
          before making a decision about the contract.
        </p>

        <h2 className="mt-14 font-serif text-3xl text-foreground">Buying locally?</h2>
        <ul className="mt-5 space-y-2 text-sm">
          <li>
            <Link to="/golden-beach" className="font-semibold text-primary underline">
              Golden Beach and Caloundra conveyancing
            </Link>{" "}
            — our principal office, Sunshine Coast purchases and sales.
          </li>
          <li>
            <Link
              to="/conveyancing/$suburb"
              params={{ suburb: "redcliffe" }}
              className="font-semibold text-primary underline"
            >
              Redcliffe conveyancing
            </Link>{" "}
            — our peninsula office at Scarborough, serving Moreton Bay.
          </li>
          <li>
            <Link to="/pexa-settlement" className="font-semibold text-primary underline">
              How PEXA settlement works in Queensland
            </Link>
          </li>
          <li>
            <Link to="/what-does-a-conveyancer-do-qld" className="font-semibold text-primary underline">
              What a conveyancer does in Queensland
            </Link>
          </li>
          <li>
            <Link to="/fees" className="font-semibold text-primary underline">
              Our fixed fees and disbursements
            </Link>
          </li>
        </ul>

        <PeopleStrip slugs={["erin-wilkinson", "michael-klein"]} heading="Who will act for you" />

        <h2 className="mt-14 font-serif text-3xl text-foreground">Stamp duty questions</h2>
        <div className="mt-6">
          <FaqList items={FAQS} />
        </div>

        <div className="mt-14">
          <EnquiryForm heading="Get your duty and total costs confirmed in writing" />
        </div>
      </section>

      <CtaBand
        title="Send us the contract before you sign"
        body={`We will confirm the exact duty, any concession you qualify for and the fixed legal fee. Call ${FIRM.phone}.`}
      />
      <div className="h-20" />
    </>
  );
}
